import React from "react";
import { ReviewRow, ReviewDefaults, IdentityType } from "../types/review";
import { patchReview } from "../api/reviewApi";

interface Props {
  row: ReviewRow;
  stage: 1 | 2;
  cycleName: string;
  toolName: string;
  reviewerUpn: string;
  reviewerDisplayName: string;
  /** Default justifications fetched from /api/config/public. Empty strings if not configured. */
  defaults: ReviewDefaults;
  onUpdated: (updatedRow: ReviewRow) => void;
  highlight?: boolean; // true = pending decision, renders with a yellow-tinted background
}

const STAGE1_DECISIONS = ["Approve", "Deny", "Don't Know"] as const;
const STAGE2_DECISIONS = ["Approve", "Deny"] as const;

/**
 * F27: an Approve/Deny only counts as a completed review when it carries a
 * non-blank justification. A blank-justification Approve/Deny is treated as
 * NOT reviewed (it escalates at S1 / is left undecided at S2). This helper is
 * the single source of truth the UI uses to decide whether to warn.
 */
function needsJustification(decision: string, justification: string): boolean {
  return (decision === "Approve" || decision === "Deny") && justification.trim() === "";
}

// ── Flag icons ────────────────────────────────────────────────────────────
// Four FIXED slots, always in the same order, so icons line up across rows:
//   1 identity (External / Service Account / Service Principal / Device / Contact
//     - mutually exclusive, so they share a slot), 2 account disabled,
//   3 long-term leave, 4 self-review. A row shows at most four icons.
// Every icon has a tooltip (title) and a matching aria-label.
interface Flag { icon: string; label: string }

const IDENTITY_FLAGS: Partial<Record<IdentityType, Flag>> = {
  external:          { icon: "🌐", label: "External account — a guest, or not on an internal domain" },
  service_account:   { icon: "🤖", label: "Non-employee account (NEA)" },
  service_principal: { icon: "🧩", label: "Service principal — an application identity, not a person" },
  device:            { icon: "💻", label: "Device" },
  contact:           { icon: "📇", label: "Contact — a mail contact, not a user account" },
};

const FLAG_DISABLED:    Flag = { icon: "🔒",  label: "Entra account disabled" };
const FLAG_LEAVE:       Flag = { icon: "🏖️", label: "On long-term leave" };
const FLAG_SELF_REVIEW: Flag = { icon: "👤",  label: "Self-review — auto-escalated" };

const FlagSlot: React.FC<{ flag: Flag | null }> = ({ flag }) => (
  <span style={{ display: "inline-block", width: 24, textAlign: "center" }}>
    {flag && <span title={flag.label} aria-label={flag.label} role="img">{flag.icon}</span>}
  </span>
);

const MemberRow: React.FC<Props> = ({
  row, stage, cycleName, toolName, reviewerUpn, reviewerDisplayName, defaults, onUpdated, highlight,
}) => {
  const currentDecision = stage === 1 ? row.s1Decision : row.s2Decision;
  const currentJustification = stage === 1 ? row.s1Justification : row.s2Justification;

  const [decision, setDecision] = React.useState(currentDecision ?? "");
  const [justification, setJustification] = React.useState(currentJustification ?? "");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // The decision that failed to save, kept so Retry can resend exactly it.
  const [failed, setFailed] = React.useState<{ dec: string; just: string } | null>(null);

  const isLocked = stage === 1 && row.isSelfReview === true;
  const decisions = stage === 1 ? STAGE1_DECISIONS : STAGE2_DECISIONS;

  async function handleDecisionChange(newDecision: string) {
    setDecision(newDecision);
    // Look up the default justification for this decision from the runtime-
    // fetched defaults (system_settings via /api/config/public). If a default
    // is empty (admin hasn't configured it), the reviewer just gets a blank
    // field to fill in themselves.
    let defaultJustification = "";
    if (newDecision === "Approve") defaultJustification = defaults.approveDefaultJustification;
    else if (newDecision === "Deny") defaultJustification = defaults.denyDefaultJustification;
    setJustification(defaultJustification);

    if (newDecision) {
      await save(newDecision, defaultJustification);
    } else {
      // Reverted to "— Select —": update parent state so yellow highlight reappears.
      // We don’t write back to the server — the last saved decision remains until a real one is chosen.
      const clearedRow: ReviewRow = {
        ...row,
        ...(stage === 1
          ? { s1Decision: null, s1Justification: null }
          : { s2Decision: null, s2Justification: null }
        ),
      };
      onUpdated(clearedRow);
    }
  }

  async function handleJustificationBlur() {
    if (decision) await save(decision, justification);
  }

  async function save(dec: string, just: string) {
    if (!dec) return;
    setSaving(true);
    setError(null);
    setFailed(null);
    try {
      await patchReview(cycleName, toolName, row.id, {
        stage,
        decision: dec,
        justification: just,
        reviewerUpn,
        reviewerDisplayName,
      });
      const updatedRow: ReviewRow = {
        ...row,
        ...(stage === 1
          ? { s1Decision: dec, s1Justification: just, s1ReviewedBy: reviewerDisplayName, s1ReviewedDate: new Date().toISOString() }
          : { s2Decision: dec, s2Justification: just, s2ReviewedBy: reviewerDisplayName, s2ReviewedDate: new Date().toISOString() }
        ),
      };
      onUpdated(updatedRow);
    } catch {
      setError("Not saved — the decision was not recorded.");
      setFailed({ dec, just });
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr style={{
      opacity: saving ? 0.6 : 1,
      // A failed save turns the row red (not the yellow of "pending"/"Saving…"),
      // so a row whose decision was NOT recorded can't look decided.
      background: failed ? "#fde7e9" : isLocked ? "#f5f5f5" : highlight ? "#fffbe6" : "white",
      borderLeft: failed ? "3px solid #d13438" : highlight ? "3px solid #ffc107" : "3px solid transparent",
      transition: "background 0.2s",
    }}>
      <td>{row.memberDisplayName}</td>
      <td>{row.memberUpn}</td>
      <td>{row.teamName}</td>
      <td>
        <FlagSlot flag={(row.identityType && IDENTITY_FLAGS[row.identityType]) || null} />
        <FlagSlot flag={row.accountDisabled ? FLAG_DISABLED : null} />
        <FlagSlot flag={row.onLongTermLeave ? FLAG_LEAVE : null} />
        <FlagSlot flag={row.isSelfReview ? FLAG_SELF_REVIEW : null} />
      </td>
      <td>
        {isLocked ? (
          <span style={{ color: "#888" }}>Auto-escalated</span>
        ) : (
          <select
            value={decision}
            onChange={(e) => handleDecisionChange(e.target.value)}
            disabled={saving}
            style={{ padding: "4px 8px" }}
          >
            <option value="">— Select —</option>
            {decisions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        )}
      </td>
      <td>
        {!isLocked && (
          <>
            <input
              type="text"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              onBlur={handleJustificationBlur}
              disabled={saving || !decision || decision === "Don't Know"}
              placeholder={decision === "Don't Know" ? "—" : "Justification (required)"}
              style={{
                width: "100%", padding: "4px 8px",
                border: needsJustification(decision, justification)
                  ? "1px solid #d13438"
                  : "1px solid #ccc",
              }}
            />
            {needsJustification(decision, justification) && (
              <div style={{ color: "#d13438", fontSize: "12px", marginTop: "4px", lineHeight: 1.3 }}>
                ⚠️ A justification is required for “{decision}”. Until you add one,
                this member will be counted as <strong>not reviewed</strong>
                {stage === 1 ? " and escalated to your Team Lead." : " at the deadline."}
              </div>
            )}
          </>
        )}
      </td>
      <td>
        {error && (
          <div>
            <div style={{ color: "#a4262c", fontSize: "12px", fontWeight: 600 }}>⚠️ NOT SAVED</div>
            <div style={{ color: "#a4262c", fontSize: "12px", marginBottom: 4 }}>{error}</div>
            {failed && (
              <button
                onClick={() => save(failed.dec, failed.just)}
                disabled={saving}
                style={{
                  background: "#d13438", color: "white", border: "none", borderRadius: 4,
                  padding: "4px 12px", fontSize: "12px", cursor: "pointer",
                }}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {saving && <span style={{ color: "#888", fontSize: "12px" }}>Saving…</span>}
      </td>
    </tr>
  );
};

export default MemberRow;
